import { useState, useRef } from 'react'
import { Upload, Download, X, Sliders } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function ImageCompressor() {
  const [selectedFile, setSelectedFile] = useState(null)
  const [compressedImage, setCompressedImage] = useState(null)
  const [quality, setQuality] = useState(80)
  const [originalSize, setOriginalSize] = useState(0)
  const [compressedSize, setCompressedSize] = useState(0)
  const [isCompressing, setIsCompressing] = useState(false)
  const fileInputRef = useRef(null)

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const handleFileSelect = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.')
      return
    }
    setSelectedFile(file)
    setOriginalSize(file.size)
    setCompressedImage(null)
    setCompressedSize(0)
  }

  const compressImage = () => {
    if (!selectedFile) return

    setIsCompressing(true)
    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const maxWidth = 1920
        const maxHeight = 1920

        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width
        let height = img.height

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = width * ratio
          height = height * ratio
        }

        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        // Convert to blob with quality setting
        const outputFormat = selectedFile.type === 'image/png' ? 'image/jpeg' : selectedFile.type
        const qualityValue = quality / 100

        canvas.toBlob(
          (blob) => {
            const url = URL.createObjectURL(blob)
            setCompressedImage({ url, blob })
            setCompressedSize(blob.size)
            setIsCompressing(false)
          },
          outputFormat,
          qualityValue
        )
      }
      img.src = e.target.result
    }

    reader.readAsDataURL(selectedFile)
  }

  const downloadImage = () => {
    if (!compressedImage) return

    const link = document.createElement('a')
    link.href = compressedImage.url
    link.download = `compressed-${selectedFile.name}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const clearFile = () => {
    setSelectedFile(null)
    setCompressedImage(null)
    setOriginalSize(0)
    setCompressedSize(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const compressionRatio = originalSize > 0 && compressedSize > 0
    ? ((1 - compressedSize / originalSize) * 100).toFixed(1)
    : 0

  return (
    <div className="space-y-6">
      <Card
        className={`cursor-pointer transition-all ${
          selectedFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-primary-400'
        }`}
        onClick={() => fileInputRef.current?.click()}
      >
        <Card.Body className="text-center py-8">
          {!selectedFile ? (
            <div>
              <div className="mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-50 rounded-full">
                  <Upload className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
              <p className="text-base font-semibold text-gray-900">Click to upload image</p>
              <p className="text-sm text-gray-500 mt-1">Select an image to compress</p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="text-left">
                <p className="text-base font-semibold text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">Original: {formatFileSize(originalSize)}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  clearFile()
                }}
                className="ml-auto p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) handleFileSelect(e.target.files[0])
        }}
      />

      {selectedFile && (
        <>
          <Card>
            <Card.Body>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">Compression Quality: {quality}%</label>
                  <Sliders className="w-5 h-5 text-gray-500" />
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={quality}
                  onChange={(e) => setQuality(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Smaller file (10%)</span>
                  <span>Better quality (100%)</span>
                </div>
              </div>

              <Button
                onClick={compressImage}
                variant="primary"
                className="w-full"
                disabled={isCompressing}
              >
                {isCompressing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Compressing...
                  </>
                ) : (
                  'Compress Image'
                )}
              </Button>
            </Card.Body>
          </Card>

          {compressedImage && (
            <Card>
              <Card.Body>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-600">Original Size</p>
                      <p className="text-lg font-semibold text-gray-900">{formatFileSize(originalSize)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Compressed Size</p>
                      <p className="text-lg font-semibold text-green-600">{formatFileSize(compressedSize)}</p>
                    </div>
                  </div>

                  {compressionRatio > 0 && (
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600">Size Reduction</p>
                      <p className="text-2xl font-bold text-green-600">{compressionRatio}%</p>
                    </div>
                  )}

                  <div className="text-center">
                    <div className="inline-block p-4 bg-gray-100 rounded-lg mb-4">
                      <img src={compressedImage.url} alt="Compressed" className="max-w-full h-auto max-h-96" />
                    </div>
                    <Button onClick={downloadImage} variant="primary" size="lg" className="w-full">
                      <Download className="w-5 h-5 mr-2" />
                      Download Compressed Image
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

